import React, { useState, useEffect, useContext } from "react";
import { X, Save, Eye, Edit, Calendar, Camera, FileText, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import InspectionModal from "./InspectionModal";
import { UserContext } from "../../contexts/UserContext";

const EquipmentModal = ({
  isOpen,
  onClose,
  mode, // 'create', 'edit', 'view', 'duplicate'
  equipmentType, // 'safety', 'types'
  selectedItem, // Can be full object (for create/duplicate) or just ID (for edit/view)
  onSave,
  safetyEquipmentTypes = []
}) => {
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [currentMode, setCurrentMode] = useState(mode);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [fullEquipmentData, setFullEquipmentData] = useState(null); // Store fetched detailed data
  const [addInspectionOnCreate, setAddInspectionOnCreate] = useState(false);
  const [inspectionData, setInspectionData] = useState({
    inspection_date: '',
    inspector: '',
    validity_date: '',
    status: 'ok',
    comments: '',
    image: null,
    inspection_type: 'internal', // 'internal' or 'external'
    external_company_name: '',
    inspection_file: null // PDF file for external inspections
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isMultiCreate, setIsMultiCreate] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [bulkCreationData, setBulkCreationData] = useState([]);
  const [serialCheckLoading, setSerialCheckLoading] = useState(false);
  const [serialWarningInline, setSerialWarningInline] = useState(null);
  const [checkTimeout, setCheckTimeout] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

  // Get logged in user's full name from context
  const loggedInUserName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '' : '';

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Format datetime for display
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get inspection status with color
  const getInspectionStatus = (inspection) => {
    if (!inspection || !inspection.validity_date) {
      return { text: 'No inspection', color: 'gray' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validityDate = new Date(inspection.validity_date);
    validityDate.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));

    if (inspection.status === 'non-ok') {
      return { text: 'Failed', color: 'red' };
    } else if (daysUntilExpiry < 0) {
      return { text: `Expired ${Math.abs(daysUntilExpiry)} days ago`, color: 'red' };
    } else if (daysUntilExpiry === 0) {
      return { text: 'Expires today', color: 'orange' };
    } else if (daysUntilExpiry <= 30) {
      return { text: `Expires in ${daysUntilExpiry} days`, color: 'yellow' };
    } else {
      return { text: `Valid for ${daysUntilExpiry} days`, color: 'green' };
    }
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };


  // Get date one year from today in YYYY-MM-DD format
  const getOneYearFromToday = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  // Get today's date and time in YYYY-MM-DDTHH:MM format (for inspection datetime)
  const getTodayDateTime = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Update current mode when mode prop changes
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  // Fetch detailed equipment data when modal opens in edit/view/duplicate mode
  useEffect(() => {
    const fetchEquipmentDetails = async () => {
      // Only fetch if modal is open and not in create mode
      if (!isOpen || mode === 'create') {
        setFullEquipmentData(null);
        return;
      }

      // If selectedItem is a number (ID), fetch full details from API
      // This applies to edit, view, and duplicate modes
      if (typeof selectedItem === 'number') {
        setLoadingDetails(true);
        setError(null);
        try {
          const token = localStorage.getItem('access_token');
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          };

          const response = await axios.get(`${API_BASE_URL}/api/safetyequipment/${selectedItem}/`, config);
          setFullEquipmentData(response.data);
        } catch (err) {
          console.error('Error fetching equipment details:', err);
          setError('Failed to load equipment details');
          setFullEquipmentData(null);
        } finally {
          setLoadingDetails(false);
        }
      } else if (selectedItem && typeof selectedItem === 'object') {
        // Fallback: if selectedItem is already an object, use it directly
        // (This shouldn't happen anymore, but keeping for backwards compatibility)
        setFullEquipmentData(selectedItem);
      } else {
        setFullEquipmentData(null);
      }
    };

    fetchEquipmentDetails();
  }, [isOpen, mode, selectedItem, API_BASE_URL]);

  // Helper function to get current user's company ID
  const getCurrentUserCompanyId = () => {
    // PRIORITY 1: Use UserContext (most reliable - always up to date)
    if (user && user.company) {
      return typeof user.company === 'number' ? user.company : parseInt(user.company);
    }

    // PRIORITY 2: Infer from safetyEquipmentTypes (they belong to user's company)
    if (safetyEquipmentTypes && safetyEquipmentTypes.length > 0 && safetyEquipmentTypes[0].company) {
      return safetyEquipmentTypes[0].company;
    }

    // PRIORITY 3: Fallback to sessionStorage
    const fullUserData = sessionStorage.getItem('fullUserData');
    if (fullUserData) {
      const userData = JSON.parse(fullUserData);
      if (userData && userData.company) {
        return typeof userData.company === 'number' ? userData.company : parseInt(userData.company);
      }
    }

    // PRIORITY 4: Fallback to localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser && parsedUser.company) {
        return typeof parsedUser.company === 'number' ? parsedUser.company : parseInt(parsedUser.company);
      }
    }

    return null;
  };

  // Helper function to check if user can edit the equipment (only own company equipment)
  const canEditEquipment = () => {
    const dataToCheck = fullEquipmentData || selectedItem;
    if (!dataToCheck || typeof dataToCheck !== 'object') return false;
    const currentUserCompanyId = getCurrentUserCompanyId();
    return dataToCheck.company === currentUserCompanyId;
  };

  // Switch to edit mode (only if user has permission)
  const switchToEditMode = () => {
    if (canEditEquipment()) {
      setCurrentMode('edit');
    }
  };

  // Initialize form data
  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        setFormData(getEmptyFormData());
        setAvailableModels([]);
        resetInspectionData();
        resetBulkCreationData();
        // Clear serial number check states
        setSerialWarningInline(null);
        setSerialCheckLoading(false);
        if (checkTimeout) {
          clearTimeout(checkTimeout);
          setCheckTimeout(null);
        }
      } else if (mode === 'duplicate' && fullEquipmentData) {
        // For duplicate mode, copy only specific fields: type, model, size, manufacture_date, initials
        const emptyData = getEmptyFormData();
        const duplicatedData = {
          ...emptyData,
          safety_equipment_type: fullEquipmentData.safety_equipment_type || '',
          safety_equipment_model: fullEquipmentData.safety_equipment_model || '',
          safety_equipment_size: fullEquipmentData.safety_equipment_size || '',
          safety_equipment_manufacture_date: fullEquipmentData.safety_equipment_manufacture_date || '',
          safety_equipment_initials: fullEquipmentData.safety_equipment_initials || ''
        };
        setFormData(duplicatedData);
        resetInspectionData();
        resetBulkCreationData();
        // Clear serial number check states
        setSerialWarningInline(null);
        setSerialCheckLoading(false);
        if (checkTimeout) {
          clearTimeout(checkTimeout);
          setCheckTimeout(null);
        }

        // Fetch models for the selected type
        if (duplicatedData.safety_equipment_type) {
          fetchModelsForType(duplicatedData.safety_equipment_type);
        }
      } else if ((mode === 'edit' || mode === 'view') && fullEquipmentData) {
        // Use fullEquipmentData for edit/view modes
        let editData = { ...fullEquipmentData };

        // Handle new vs legacy data structure
        if (equipmentType === 'safety') {
          // If item has safety_equipment_model, use the new structure
          if (fullEquipmentData.safety_equipment_model) {
            editData.safety_equipment_model = fullEquipmentData.safety_equipment_model;
          } else {
            // For legacy items without model reference, clear the model field
            editData.safety_equipment_model = '';
          }
        }

        setFormData(editData);

        // Fetch models for the selected type in edit mode
        if (fullEquipmentData.safety_equipment_type) {
          fetchModelsForType(fullEquipmentData.safety_equipment_type);
        }
      }
      setError(null);
    }
  }, [isOpen, mode, fullEquipmentData, equipmentType]);

  // Cleanup timeout when component unmounts or closes
  useEffect(() => {
    return () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
    };
  }, [checkTimeout]);

  const getEmptyFormData = () => {
    switch (equipmentType) {
      case 'safety':
      case 'safety-external':
        return {
          safety_equipment_type: '',
          safety_equipment_model: '', // Now references SafetyEquipmentModel ID
          safety_equipment_size: '',
          safety_equipment_vi_number: '',
          safety_equipment_initials: '',
          safety_equipment_status: 'available',
          safety_equipment_serial_number: '',
          nfc_tag_number: '', // New field
          safety_equipment_location: '',
          safety_equipment_group: '',
          safety_equipment_comment: '',
          safety_equipment_manufacture_date: ''
        };
      case 'types':
        return {
          name: '',
          description: ''
        };
      default:
        return {};
    }
  };

  // Fetch models when equipment type changes
  const fetchModelsForType = async (typeId) => {
    if (!typeId) {
      setAvailableModels([]);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Find the selected company type to get its global_type_id
      const companyType = safetyEquipmentTypes.find(type => type.id === parseInt(typeId));

      console.log('🔍 DEBUG fetchModelsForType:');
      console.log('  - Selected typeId:', typeId);
      console.log('  - Found companyType:', companyType);
      console.log('  - All safetyEquipmentTypes:', safetyEquipmentTypes);

      if (!companyType || !companyType.global_type_id) {
        console.warn('⚠️  No companyType found or missing global_type_id');
        console.log('  - companyType:', companyType);
        console.log('  - global_type_id:', companyType?.global_type_id);
        setAvailableModels([]);
        return;
      }

      const currentUserCompanyId = getCurrentUserCompanyId();
      console.log('  - currentUserCompanyId:', currentUserCompanyId);
      console.log('  - Looking for models with safety_equipment_type:', companyType.global_type_id);

      const response = await axios.get(`${API_BASE_URL}/api/safetyequipmentmodel/?scope=company`, config);
      console.log('  - Total models from API:', response.data.length);
      console.log('  - Sample model:', response.data[0]);

      // Models are linked to GLOBAL types, not company types
      const modelsForType = response.data.filter(model =>
        model.safety_equipment_type === companyType.global_type_id &&
        model.company === currentUserCompanyId
      );

      console.log('  - Filtered models count:', modelsForType.length);
      console.log('  - Filtered models:', modelsForType);

      setAvailableModels(modelsForType);
    } catch (error) {
      console.error('❌ Error fetching models:', error);
      setAvailableModels([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If equipment type changes, fetch models and reset model selection
    if (name === 'safety_equipment_type') {
      fetchModelsForType(value);
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        safety_equipment_model: '' // Reset model selection when type changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleInspectionInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'image') {
      const file = files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setError('Please select a valid image file');
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError('Image file size must be less than 5MB');
          return;
        }

        setInspectionData(prev => ({
          ...prev,
          image: file
        }));

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setInspectionData(prev => ({
          ...prev,
          image: null
        }));
        setImagePreview(null);
      }
    } else if (name === 'inspection_file') {
      const file = files[0];
      if (file) {
        // Validate file type
        if (file.type !== 'application/pdf') {
          setError('Please select a valid PDF file');
          return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError('PDF file size must be less than 10MB');
          return;
        }

        setInspectionData(prev => ({
          ...prev,
          inspection_file: file
        }));
      } else {
        setInspectionData(prev => ({
          ...prev,
          inspection_file: null
        }));
      }
    } else if (name === 'status') {
      if (value === 'non-ok') {
        // When status is set to 'non-ok', automatically set validity date to today
        setInspectionData(prev => ({
          ...prev,
          [name]: value,
          validity_date: getTodayDate()
        }));
      } else {
        // When status is 'ok', reset validity date to one year from today
        setInspectionData(prev => ({
          ...prev,
          [name]: value,
          validity_date: getOneYearFromToday()
        }));
      }
    } else {
      setInspectionData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const resetInspectionData = () => {
    setInspectionData({
      inspection_date: getTodayDateTime(),
      inspector: loggedInUserName || '', // Use state instead of function
      validity_date: getOneYearFromToday(),
      status: 'ok',
      comments: '',
      image: null
    });
    setImagePreview(null);
    setAddInspectionOnCreate(false);
  };

  const resetBulkCreationData = () => {
    setQuantity(1);
    setIsMultiCreate(false);
    setCurrentItemIndex(0);
    setBulkCreationData([]);
  };

  const checkForExistingSerialNumber = async (serialNumber) => {
    console.log('DEBUG: checkForExistingSerialNumber called with:', typeof serialNumber, serialNumber);
    
    if (!serialNumber || serialNumber.trim() === '') {
      return null;
    }

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Search for existing equipment with the same serial number
      const response = await axios.get(`${API_BASE_URL}/api/safetyequipment/?search=${encodeURIComponent(serialNumber.trim())}`, config);
      
      console.log('DEBUG: Full API response structure:', response.data);
      console.log('DEBUG: API response type:', typeof response.data);
      console.log('DEBUG: Has results?', 'results' in response.data);
      console.log('DEBUG: API response length:', response.data?.results?.length);
      
      // Handle both paginated (results) and direct array responses
      const items = response.data.results || response.data;
      
      if (items && Array.isArray(items)) {
        // Manual check with detailed logging
        const searchSerial = serialNumber.trim();
        console.log('DEBUG: Looking for exact match with serial:', `"${searchSerial}"`);
        console.log('DEBUG: Searching through', items.length, 'items');
        
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const itemSerial = item.safety_equipment_serial_number;
          console.log(`DEBUG: Item ${i}: ID=${item.id}, Serial="${itemSerial}", Match=${itemSerial === searchSerial}`);
          
          if (itemSerial === searchSerial) {
            console.log('DEBUG: EXACT MATCH FOUND!', item);
            return item;
          }
        }
        
        console.log('DEBUG: No exact match found in', items.length, 'results');
        return null;
      }
      
      return null;
    } catch (error) {
      console.error('Error checking for existing serial number:', error);
      return null;
    }
  };

  const generateInspectionPDF = async (inspectionData, equipmentData, loggedUserCompanyData) => {
    try {
      console.log('🔄 Generating PDF for inspection using backend...');
      console.log('📊 Equipment ID:', equipmentData.id);

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      };

      const response = await axios.post(`${API_BASE_URL}/api/ppe-report/`, {
        equipment_ids: [equipmentData.id],
        report_type: 'single'
      }, config);

      console.log('✅ PDF generated successfully using backend');
      return response.data;

    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      throw new Error(`Failed to generate PDF report: ${error.response?.status || error.message}`);
    }
  };

  const createInspectionForEquipment = async (equipmentId, config) => {
    // Validate inspection data based on inspection type
    if (inspectionData.inspection_type === 'internal') {
      if (!inspectionData.inspection_date || !inspectionData.inspector || !inspectionData.validity_date) {
        throw new Error('Please fill in all required inspection fields (Date, Inspector, Validity Date)');
      }
    } else if (inspectionData.inspection_type === 'external') {
      if (!inspectionData.inspection_date || !inspectionData.inspector || !inspectionData.validity_date || !inspectionData.external_company_name) {
        throw new Error('Please fill in all required inspection fields (Date, Inspector, Validity Date, External Company)');
      }
    }

    try {
      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('inspection_date', inspectionData.inspection_date);
      formDataToSubmit.append('inspector', inspectionData.inspector);
      formDataToSubmit.append('validity_date', inspectionData.validity_date);
      formDataToSubmit.append('status', inspectionData.status);
      formDataToSubmit.append('comments', inspectionData.comments || '');
      formDataToSubmit.append('safety_equipment', equipmentId);
      formDataToSubmit.append('inspection_type', inspectionData.inspection_type);

      if (inspectionData.inspection_type === 'internal') {
        // For internal inspections, inspector is auto-filled from logged-in user
        formDataToSubmit.append('inspector', inspectionData.inspector);
      } else if (inspectionData.inspection_type === 'external') {
        // For external inspections, add external company name and PDF file
        formDataToSubmit.append('inspector', inspectionData.inspector);
        formDataToSubmit.append('external_company_name', inspectionData.external_company_name);
        if (inspectionData.inspection_file) {
          formDataToSubmit.append('inspection_file', inspectionData.inspection_file);
        }
      }

      if (inspectionData.image) {
        formDataToSubmit.append('inspection_photo', inspectionData.image);
      }

      // Update config for multipart/form-data
      const inspectionConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        }
      };

      const response = await axios.post(`${API_BASE_URL}/api/inspection/`, formDataToSubmit, inspectionConfig);
      console.log('Inspection created successfully');

      // Only generate PDF for internal inspections
      if (inspectionData.inspection_type === 'internal') {
        console.log('📄 Generating PDF for internal inspection');
        try {
          // Fetch equipment data for PDF generation
          const equipmentResponse = await axios.get(`${API_BASE_URL}/api/safetyequipment/${equipmentId}/`, config);
          const equipmentData = equipmentResponse.data;
          console.log('✅ Equipment data fetched:', equipmentData);

          // Use UserContext data directly - same as InspectionModal
          console.log('📥 Getting logged user company data for PDF header...');
          console.log('🔍 UserContext data:', user);

          const loggedUserCompanyData = user || {
            company_name: 'Unknown Company',
            company: null
          };

          console.log('✅ Using user company data:', {
            companyName: loggedUserCompanyData.company_name,
            companyId: loggedUserCompanyData.company,
            hasCompanyName: !!loggedUserCompanyData.company_name,
            hasCompanyId: !!loggedUserCompanyData.company
          });

          const pdfBlob = await generateInspectionPDF(
            {
              ...response.data,
              inspection_photo: response.data.inspection_photo
            },
            equipmentData,
            loggedUserCompanyData
          );

          const pdfFormData = new FormData();
          pdfFormData.append('inspection_file', pdfBlob, 'inspection_report.pdf');

          await axios.patch(
            `${API_BASE_URL}/api/inspection/${response.data.id}/`,
            pdfFormData,
            inspectionConfig
          );
          console.log('✅ PDF generated and uploaded successfully');
        } catch (pdfError) {
          console.error('Error generating/uploading PDF:', pdfError);

          // Rollback - delete the inspection that was created without PDF
          try {
            await axios.delete(`${API_BASE_URL}/api/inspection/${response.data.id}/`, config);
            console.log('🔄 Rolled back inspection creation due to PDF generation failure');
          } catch (deleteError) {
            console.error('Error deleting inspection during rollback:', deleteError);
          }

          throw new Error('Equipment created but inspection failed - PDF generation error: ' + (pdfError.response?.data?.detail || pdfError.message));
        }
      } else {
        console.log('📄 External inspection - PDF already uploaded by user');
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      throw new Error('Equipment created but inspection failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const checkSerialInRealTime = async (serialNumber) => {
    console.log('DEBUG: checkSerialInRealTime called with:', typeof serialNumber, serialNumber);
    
    if (!serialNumber || serialNumber.trim() === '') {
      setSerialWarningInline(null);
      return;
    }

    try {
      setSerialCheckLoading(true);
      const existingEquipment = await checkForExistingSerialNumber(serialNumber);
      
      if (existingEquipment) {
        console.log('FOUND DUPLICATE:', existingEquipment.id, existingEquipment.safety_equipment_serial_number);
        setSerialWarningInline(existingEquipment);
      } else {
        console.log('NO DUPLICATE FOUND FOR:', serialNumber);
        setSerialWarningInline(null);
      }
    } catch (error) {
      console.error('Error checking serial in real time:', error);
      setSerialWarningInline(null);
    } finally {
      setSerialCheckLoading(false);
    }
  };

  const handleSerialNumberChange = (value) => {
    console.log('DEBUG: handleSerialNumberChange called with:', typeof value, value);
    
    // Update form data immediately
    setFormData(prev => ({ ...prev, safety_equipment_serial_number: value }));
    
    // Clear previous timeout
    if (checkTimeout) {
      clearTimeout(checkTimeout);
    }
    
    // Clear previous warning if empty
    if (!value || value.trim() === '') {
      setSerialWarningInline(null);
      setSerialCheckLoading(false);
      return;
    }
    
    // Set a debounced check (500ms after user stops typing)
    const newTimeout = setTimeout(() => {
      console.log('DEBUG: About to call checkSerialInRealTime with:', typeof value, value);
      checkSerialInRealTime(value);
    }, 500);
    
    setCheckTimeout(newTimeout);
  };


  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('handleSave called - currentMode:', currentMode, 'quantity:', quantity, 'isMultiCreate:', isMultiCreate);

      // Check if we're in bulk creation mode and this is the first item
      if ((currentMode === 'create' || currentMode === 'duplicate') && quantity > 1 && !isMultiCreate) {
        console.log('Entering bulk creation mode');
        // Initialize bulk creation
        setIsMultiCreate(true);
        // Don't reset currentItemIndex here - use it as starting point
        setBulkCreationData([]);
        
        // Validate and save the first item
        const validationResult = validateFormData();
        if (validationResult.hasErrors) {
          setError(validationResult.errorMessage);
          setLoading(false);
          return;
        }
        
        // Continue with single item creation for now
        await createSingleItem(true); // Skip onSave during bulk creation
        
        // If we have more items to create, show next form
        const nextIndex = 1; // First item created, next is index 1
        console.log('Bulk creation initialized: next index:', nextIndex, 'quantity:', quantity);
        if (nextIndex < quantity) {
          console.log('Setting currentItemIndex to:', nextIndex, 'and staying in modal');
          setCurrentItemIndex(nextIndex);
          // Reset unique fields for next item
          resetUniqueFields();
          setLoading(false);
          return;
        } else {
          // All items created, close modal
          console.log('All items created, closing modal');
          setIsMultiCreate(false);
          onSave({}); // Call onSave to refresh the parent component
          onClose();
          return;
        }
      } else if (isMultiCreate) {
        // We're in the middle of bulk creation
        console.log('Continuing bulk creation - currentItemIndex:', currentItemIndex, 'quantity:', quantity);
        const validationResult = validateFormData();
        if (validationResult.hasErrors) {
          setError(validationResult.errorMessage);
          setLoading(false);
          return;
        }
        
        await createSingleItem(true); // Skip onSave during bulk creation
        
        // Check if we have more items to create
        const nextIndex = currentItemIndex + 1;
        if (nextIndex < quantity) {
          setCurrentItemIndex(nextIndex);
          resetUniqueFields();
          setLoading(false);
          return;
        } else {
          // All items created, close modal
          console.log('All bulk items created, closing modal');
          setIsMultiCreate(false);
          onSave({}); // Call onSave to refresh the parent component
          onClose();
          return;
        }
      } else {
        // Single item creation (normal flow)
        const validationResult = validateFormData();
        if (validationResult.hasErrors) {
          setError(validationResult.errorMessage);
          setLoading(false);
          return;
        }
        
        await createSingleItem();
        onClose();
        return;
      }
    } catch (error) {
      console.error('Error saving equipment:', error);
      setError('Failed to save equipment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const validateFormData = () => {
    const requiredFields = [];
    
    if (equipmentType === 'safety') {
      if (!formData.safety_equipment_type) {
        requiredFields.push('Equipment Type');
      }
      
      if (!formData.safety_equipment_model) {
        requiredFields.push('Equipment Model');
      }
      
      if (!formData.safety_equipment_serial_number || formData.safety_equipment_serial_number.trim() === '') {
        requiredFields.push('Serial Number');
      }
      
      if (!formData.safety_equipment_manufacture_date) {
        requiredFields.push('Production Date');
      } else {
        // Validate that manufacture date is not in the future
        const manufactureDate = new Date(formData.safety_equipment_manufacture_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
        
        if (manufactureDate > today) {
          requiredFields.push('Production Date cannot be in the future');
        }
      }

      // Validate inspection fields if inspection is being added
      if (addInspectionOnCreate) {
        if (!inspectionData.inspection_date) {
          requiredFields.push('Inspection Date');
        }
        if (!inspectionData.inspector || inspectionData.inspector.trim() === '') {
          requiredFields.push('Inspector');
        }
        if (!inspectionData.validity_date) {
          requiredFields.push('Inspection Validity Date');
        } else if (inspectionData.inspection_type === 'internal') {
          // For internal inspections (perform now), validity date cannot be in the past
          const validityDate = new Date(inspectionData.validity_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
          validityDate.setHours(0, 0, 0, 0);

          if (validityDate < today) {
            requiredFields.push('Inspection Validity Date cannot be in the past');
          }
        }
      }
    }
    
    return {
      hasErrors: requiredFields.length > 0,
      errorMessage: requiredFields.length > 0 ? `Please fill in the following required fields:\n• ${requiredFields.join('\n• ')}` : null
    };
  };

  const resetUniqueFields = () => {
    if (equipmentType === 'safety') {
      setFormData(prev => ({
        ...prev,
        safety_equipment_serial_number: '',
        safety_equipment_vi_number: '',
        nfc_tag_number: ''
      }));
    }
  };

  const createSingleItem = async (skipOnSave = false) => {
    try {

      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      let endpoint = '';
      switch (equipmentType) {
        case 'safety':
        case 'safety-external':
          endpoint = 'safetyequipment';
          break;
        case 'types':
          endpoint = 'safetyequipmenttype';
          break;
      }

      // For safety equipment, ensure we're sending the correct data structure
      let dataToSend = { ...formData };
      
      if (equipmentType === 'safety') {
        // Convert safety_equipment_model to the new structure
        if (dataToSend.safety_equipment_model) {
          // safety_equipment_model now contains the SafetyEquipmentModel ID
          // The backend will handle the relationship
        }
        
        // Handle date fields - convert empty strings to null
        const dateFields = [
          'safety_equipment_date_of_inspection',
          'safety_equipment_date_of_validity', 
          'safety_equipment_manufacture_date'
        ];
        
        dateFields.forEach(field => {
          if (dataToSend[field] === '') {
            dataToSend[field] = null;
          }
        });
        
        // Handle empty model selection
        if (dataToSend.safety_equipment_model === '') {
          dataToSend.safety_equipment_model = null;
        }
        
        // Handle other empty string fields that should be null
        const stringFields = [
          'safety_equipment_size',
          'safety_equipment_vi_number', 
          'safety_equipment_initials',
          'safety_equipment_serial_number',
          'safety_equipment_inspector',
          'safety_equipment_location',
          'safety_equipment_group',
          'safety_equipment_comment',
          'nfc_tag_number'
        ];
        
        stringFields.forEach(field => {
          if (dataToSend[field] === '') {
            dataToSend[field] = null;
          }
        });
        
        // Remove any legacy fields that shouldn't be sent
        delete dataToSend.safety_equipment_brand;
        delete dataToSend.safety_equipment_price;
        delete dataToSend.safety_equipment_model_name;
        
        console.log('DEBUG: Sending safety equipment data:', dataToSend);
      }

      let response;
      if (currentMode === 'create' || currentMode === 'duplicate') {
        response = await axios.post(`${API_BASE_URL}/api/${endpoint}/`, dataToSend, config);
        
        // If creating safety equipment and inspection is requested, create inspection
        if (equipmentType === 'safety' && addInspectionOnCreate && response.data.id) {
          await createInspectionForEquipment(response.data.id, config);
        }
      } else if (currentMode === 'edit') {
        const equipmentId = fullEquipmentData?.id || (typeof selectedItem === 'number' ? selectedItem : selectedItem?.id);
        response = await axios.patch(`${API_BASE_URL}/api/${endpoint}/${equipmentId}/`, dataToSend, config);
      }

      console.log('createSingleItem: skipOnSave:', skipOnSave);
      if (!skipOnSave) {
        console.log('createSingleItem: calling onSave with response.data');
        onSave(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Error saving equipment:', error);
      console.error('Error response data:', error.response?.data);

      // If error already has a message (like inspection errors), use it
      if (error.message && (error.message.includes('inspection') || error.message.includes('PDF'))) {
        throw error;
      }

      let errorMessage = 'Failed to save equipment';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          // Handle field-specific errors
          const errors = Object.entries(error.response.data).map(([field, messages]) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            return `${field}: ${messageArray.join(', ')}`;
          }).join('\n');
          errorMessage = `Validation errors:\n${errors}`;
        } else {
          errorMessage = error.response.data.detail || error.response.data;
        }
      }

      throw new Error(errorMessage);
    }
  };

  const renderFormFields = () => {
    const isReadOnly = currentMode === 'view';

    switch (equipmentType) {
      case 'safety':
      case 'safety-external':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="safety_equipment_type"
                  value={formData.safety_equipment_type || ''}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select Type</option>
                  {safetyEquipmentTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Model <span className="text-red-500">*</span>
                </label>
                <select
                  name="safety_equipment_model"
                  value={formData.safety_equipment_model || ''}
                  onChange={handleInputChange}
                  disabled={isReadOnly || !formData.safety_equipment_type}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {!formData.safety_equipment_type ? 'Select Equipment Type First' : 'Select Model'}
                  </option>
                  {availableModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.brand} {model.name} {model.manufacturer && `(${model.manufacturer})`}
                    </option>
                  ))}
                </select>
                {formData.safety_equipment_type && availableModels.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    No models available for this equipment type
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Production Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="safety_equipment_manufacture_date"
                  value={formData.safety_equipment_manufacture_date || ''}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  max={getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial Number <span className="text-red-500">*</span>
                  {serialCheckLoading && (
                    <span className="ml-2 text-sm text-blue-500">
                      <div className="animate-spin inline-block h-3 w-3 border-b-2 border-blue-500 rounded-full mr-1"></div>
                      Checking...
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name="safety_equipment_serial_number"
                  value={formData.safety_equipment_serial_number || ''}
                  onChange={(e) => handleSerialNumberChange(e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 disabled:bg-gray-100 ${
                    serialWarningInline 
                      ? 'border-yellow-500 focus:ring-yellow-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {serialWarningInline && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">Serial Number Already Exists</p>
                        <div className="mt-1 text-xs text-yellow-700">
                          <p><strong>Type:</strong> {serialWarningInline.safety_equipment_type_name || 'N/A'}</p>
                          <p><strong>Model:</strong> {serialWarningInline.safety_equipment_model_name || 'N/A'}</p>
                          <p><strong>Status:</strong> {serialWarningInline.safety_equipment_status || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NFC Tag Number
                </label>
                <input
                  type="text"
                  name="nfc_tag_number"
                  value={formData.nfc_tag_number || ''}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="NFC tag identifier"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Size
                </label>
                <input
                  type="text"
                  name="safety_equipment_size"
                  value={formData.safety_equipment_size || ''}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment
              </label>
              <textarea
                name="safety_equipment_comment"
                value={formData.safety_equipment_comment || ''}
                onChange={handleInputChange}
                disabled={isReadOnly}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            {/* Inspections Section - Only show in edit/view mode for existing items */}
            {fullEquipmentData && fullEquipmentData.id && (currentMode === 'edit' || currentMode === 'view') && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Latest Equipment Inspection
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowInspectionModal(true)}
                    className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <Calendar className="h-4 w-4 mr-1.5" />
                    Manage Inspections
                  </button>
                </div>

                {fullEquipmentData.latest_inspection ? (
                  <div className="bg-white border rounded-lg p-4">
                    {/* Inspection Title */}
                    {(() => {
                      const inspectionStatus = getInspectionStatus(fullEquipmentData.latest_inspection);
                      let title = 'Valid';
                      let titleColor = 'text-green-600';

                      if (fullEquipmentData.latest_inspection.status === 'non-ok') {
                        title = 'Failed';
                        titleColor = 'text-red-600';
                      } else if (inspectionStatus.color === 'red') {
                        title = 'Expired';
                        titleColor = 'text-red-600';
                      }

                      return (
                        <h4 className={`text-lg font-semibold ${titleColor} mb-3`}>
                          {title}
                        </h4>
                      );
                    })()}

                    <div className="grid grid-cols-3 gap-4">
                      {/* Column 1: Date and Validity */}
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Inspection Date:</span>
                          <p className="text-sm">{formatDateTime(fullEquipmentData.latest_inspection.inspection_date)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Validity:</span>
                          <div className="flex items-center space-x-2">
                            {fullEquipmentData.latest_inspection.status === 'non-ok' ? (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                None
                              </span>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                getInspectionStatus(fullEquipmentData.latest_inspection).color === 'red' ? 'bg-red-100 text-red-800' :
                                getInspectionStatus(fullEquipmentData.latest_inspection).color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                getInspectionStatus(fullEquipmentData.latest_inspection).color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                getInspectionStatus(fullEquipmentData.latest_inspection).color === 'green' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {formatDate(fullEquipmentData.latest_inspection.validity_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Column 2: Inspector and Status */}
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Inspector:</span>
                          <p className="text-sm">{fullEquipmentData.latest_inspection.inspector}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Inspection status:</span>
                          <div className="flex items-center">
                            {fullEquipmentData.latest_inspection.status === 'ok' ? (
                              <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600 mr-1" />
                            )}
                            <span className={`text-sm ${fullEquipmentData.latest_inspection.status === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                              {fullEquipmentData.latest_inspection.status === 'ok' ? 'OK' : 'Non-OK'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Column 3: PDF Report */}
                      <div>
                        {fullEquipmentData.latest_inspection.inspection_file ? (
                          <div>
                            <span className="text-sm font-medium text-gray-500 block mb-2">PDF Report:</span>
                            <a
                              href={fullEquipmentData.latest_inspection.inspection_file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm underline"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 18h12V6h-4V2H4v16zM9 13V9l-2 2 2 2zm2-4v4l2-2-2-2z"/>
                              </svg>
                              Download PDF
                            </a>
                            <p className="text-xs text-gray-500 mt-1">
                              ID: INS-{String(fullEquipmentData.latest_inspection.id).padStart(6, '0')}
                            </p>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400">
                            No PDF report
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600">No inspections recorded yet</p>
                    <p className="text-xs text-gray-500 mt-1">Click "Manage Inspections" to add one</p>
                  </div>
                )}
              </div>
            )}

            {/* Quantity Section - Only for create and duplicate modes */}
            {(currentMode === 'create' || currentMode === 'duplicate') && (
              <div className="border-t border-gray-200 pt-4">
                {/* Quantity field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                    className="w-16 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {quantity > 1 && (
                  <div className="mb-4">
                    <div className="text-sm text-gray-600 bg-yellow-50 p-2 rounded">
                      <strong>Note:</strong> You'll be prompted to enter unique data (serial numbers, etc.) for each item.
                      {isMultiCreate && (
                        <div className="mt-1 font-medium text-blue-600">
                          Creating item {currentItemIndex + 1} of {quantity}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Inspection Section - separated with border */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Inspection Options</h4>
                  <div className="space-y-3">
                    {/* Perform inspection now - only visible for inspectors */}
                    {user?.inspector && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="performInspectionNow"
                          checked={addInspectionOnCreate && inspectionData.inspection_type === 'internal'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAddInspectionOnCreate(true);
                              setInspectionData({
                                inspection_date: getTodayDateTime(),
                                inspector: loggedInUserName || '',
                                validity_date: getOneYearFromToday(),
                                status: 'ok',
                                comments: '',
                                image: null,
                                inspection_type: 'internal',
                                external_company_name: '',
                                inspection_file: null
                              });
                              setImagePreview(null);
                            } else {
                              setAddInspectionOnCreate(false);
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="performInspectionNow" className="text-sm font-medium text-gray-700">
                          Perform inspection now
                        </label>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="addHistoricalInspection"
                        checked={addInspectionOnCreate && inspectionData.inspection_type === 'external'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAddInspectionOnCreate(true);
                            setInspectionData({
                              inspection_date: '',
                              inspector: '',
                              validity_date: '',
                              status: 'ok',
                              comments: '',
                              image: null,
                              inspection_type: 'external',
                              external_company_name: '',
                              inspection_file: null
                            });
                            setImagePreview(null);
                          } else {
                            setAddInspectionOnCreate(false);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="addHistoricalInspection" className="text-sm font-medium text-gray-700">
                        Add historical inspection
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            
          </div>
        );

      case 'types':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                disabled={isReadOnly}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        );

      default:
        return <div>No form available for this equipment type.</div>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full h-[95vh] overflow-hidden flex flex-col ${
        (currentMode === 'create' || currentMode === 'duplicate') && addInspectionOnCreate
          ? 'max-w-7xl'
          : 'max-w-4xl'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {currentMode === 'create' ? 'Add New' : currentMode === 'edit' ? 'Edit' : currentMode === 'duplicate' ? 'Duplicate' : 'View'} {' '}
            {equipmentType === 'types' ? 'Equipment Type' : 'Safety Equipment'}
            {isMultiCreate && (
              <span className="ml-2 text-sm text-blue-600 font-normal">
                (Item {currentItemIndex + 1} of {quantity})
              </span>
            )}
            {(currentMode === 'create' || currentMode === 'duplicate') && addInspectionOnCreate && (
              <span className="ml-2 text-sm text-green-600 font-normal">
                + Inspection
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - Two Panel Layout when inspection is enabled */}
        {(currentMode === 'create' || currentMode === 'duplicate') && addInspectionOnCreate ? (
          <div className="flex-1 overflow-hidden flex">
            {/* Left Panel - Equipment Form */}
            <div className="w-1/2 border-r flex flex-col">
              <div className="p-6 flex-1 overflow-y-auto">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading equipment details...</span>
                  </div>
                ) : (
                  <>
                    {error && (
                      <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                      </div>
                    )}
                    {renderFormFields()}
                  </>
                )}
              </div>
            </div>

            {/* Right Panel - Inspection Form */}
            <div className="w-1/2 flex flex-col">
              <div className="p-6 bg-blue-50 border-b">
                <h3 className="text-lg font-semibold text-blue-900">
                  {inspectionData.inspection_type === 'internal' ? 'Inspection - Perform Now' : 'Inspection - Historical Data'}
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  {inspectionData.inspection_type === 'internal'
                    ? 'Complete inspection details for the new equipment'
                    : 'Add historical inspection data'}
                </p>
              </div>
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inspection Date <span className="text-red-500">*</span>
                        {inspectionData.inspection_type === 'internal' && (
                          <span className="text-xs text-gray-500 ml-2">(Current date/time)</span>
                        )}
                      </label>
                      <input
                        type={inspectionData.inspection_type === 'internal' ? 'datetime-local' : 'date'}
                        name="inspection_date"
                        value={inspectionData.inspection_date}
                        onChange={handleInspectionInputChange}
                        required
                        disabled={inspectionData.inspection_type === 'internal'}
                        placeholder={inspectionData.inspection_type === 'external' ? 'YYYY-MM-DD' : ''}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                          inspectionData.inspection_type === 'internal' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inspector <span className="text-red-500">*</span>
                        {inspectionData.inspection_type === 'internal' && (
                          <span className="text-xs text-gray-500 ml-2">(Auto-filled)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        name="inspector"
                        value={inspectionData.inspector}
                        onChange={handleInspectionInputChange}
                        required
                        disabled={inspectionData.inspection_type === 'internal'}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                          inspectionData.inspection_type === 'internal' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Name of the inspector"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inspection Validity Date <span className="text-red-500">*</span>
                        {inspectionData.status === 'non-ok' && (
                          <span className="text-xs text-gray-500 ml-2">(Auto-set to today for failed inspections)</span>
                        )}
                      </label>
                      <input
                        type="date"
                        name="validity_date"
                        value={inspectionData.validity_date}
                        onChange={handleInspectionInputChange}
                        required
                        disabled={inspectionData.status === 'non-ok'}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                          inspectionData.status === 'non-ok' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        value={inspectionData.status}
                        onChange={handleInspectionInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="ok">OK</option>
                        <option value="non-ok">Non-OK</option>
                      </select>
                    </div>
                  </div>

                  {/* External Company Name - only for external inspections */}
                  {inspectionData.inspection_type === 'external' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        External Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="external_company_name"
                        value={inspectionData.external_company_name}
                        onChange={handleInspectionInputChange}
                        required={inspectionData.inspection_type === 'external'}
                        placeholder="Enter the company that performed the inspection"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments
                    </label>
                    <textarea
                      name="comments"
                      value={inspectionData.comments}
                      onChange={handleInspectionInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Additional notes about the inspection..."
                    />
                  </div>

                  {/* PDF Upload - only for external inspections */}
                  {inspectionData.inspection_type === 'external' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="inline h-4 w-4 mr-1" />
                        Inspection PDF Report
                      </label>
                      <input
                        type="file"
                        name="inspection_file"
                        accept="application/pdf"
                        onChange={handleInspectionInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      {inspectionData.inspection_file && (
                        <p className="mt-1 text-sm text-gray-600">
                          Selected: {inspectionData.inspection_file.name}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Camera className="inline h-4 w-4 mr-1" />
                      Inspection Photo
                    </label>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleInspectionInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview}
                          alt="Inspection preview"
                          className="max-w-full h-48 object-cover rounded-md border"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Single Panel Layout - Original */
          <div className="p-6 overflow-y-auto flex-1">
            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading equipment details...</span>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                  </div>
                )}
                {renderFormFields()}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <span className="text-red-500">*</span> Required fields
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {currentMode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {currentMode === 'view' && canEditEquipment() && (
              <button
                onClick={switchToEditMode}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 flex items-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
            )}
            {currentMode !== 'view' && (
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isMultiCreate ? 
                  (currentItemIndex + 1 < quantity ? 
                    `Create & Next (${currentItemIndex + 1}/${quantity})` : 
                    `Create Final Item (${quantity}/${quantity})`
                  ) : 
                  (currentMode === 'create' ? 'Create' : currentMode === 'duplicate' ? 'Create Copy' : 'Save Changes')
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inspection Modal */}
      <InspectionModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        equipmentId={fullEquipmentData?.id}
        equipmentName={fullEquipmentData ? (fullEquipmentData.safety_equipment_model_name || fullEquipmentData.safety_equipment_type_name) : ''}
        equipmentSerialNumber={fullEquipmentData?.safety_equipment_serial_number}
        equipmentData={fullEquipmentData}
        onSave={() => {
          // Refresh equipment data to get updated inspection info
          onSave && onSave();
        }}
      />
    </div>
  );
};

export default EquipmentModal;